import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import React, { useEffect, useRef, useState } from 'react'

import { GameUIMessageType, ShowCircleAnimationCompleteMessage, ShowCircleAnimationMessage } from 'game-ui-types'

interface UseCircleProgressAnimationProps {
    ref: React.RefObject<SVGCircleElement | null>
    duration: number
    isRunning: boolean
    onComplete?: () => void
}

const useCircleProgressAnimation = ({ ref, duration, isRunning, onComplete }: UseCircleProgressAnimationProps) => {
    const [externalDuration, setExternalDuration] = useState<number | null>(null)
    const isCircleRunning = externalDuration !== null
    const tweenRef = useRef<gsap.core.Tween | null>(null)

    const killCircleAnimation = () => {
        if (externalDuration === null) return

        tweenRef.current?.kill()
        tweenRef.current = null

        if (ref.current) gsap.set(ref.current, { strokeDashoffset: 0, opacity: 0 })

        setExternalDuration(null)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const data = event.data as ShowCircleAnimationMessage

            if (data?.type !== GameUIMessageType.SHOW_CIRCLE_ANIMATION) return

            const requested = data.payload?.duration

            if (typeof requested !== 'number' || requested <= 0) return

            setExternalDuration(requested)
        }

        window.addEventListener('message', handleMessage)

        return () => window.removeEventListener('message', handleMessage)
    }, [])

    useGSAP(() => {
        const circle = ref.current

        if (!circle || (!isRunning && !isCircleRunning)) return

        let circleLength = 0

        try {
            circleLength = circle.getTotalLength()
        } catch (err) {
            console.log(err)
            circle.style.display = 'none'

            return
        }

        gsap.killTweensOf(circle)

        tweenRef.current = gsap.fromTo(
            circle,
            { strokeDasharray: circleLength, strokeDashoffset: 0 },
            {
                strokeDashoffset: -circleLength,
                duration: externalDuration ?? duration,
                ease: 'linear',
                onStart: () => {
                    gsap.set(circle, { strokeDashoffset: 0, opacity: 1 })
                },
                onComplete: () => {
                    gsap.set(circle, { strokeDashoffset: 0, opacity: 0 })

                    if (isCircleRunning) {
                        setExternalDuration(null)

                        const message: ShowCircleAnimationCompleteMessage = {
                            type: GameUIMessageType.SHOW_CIRCLE_ANIMATION_COMPLETE,
                        }

                        window.postMessage(message, '*')

                        return
                    }

                    onComplete?.()
                },
            }
        )
    }, [isRunning, externalDuration])

    return { isCircleRunning, killCircleAnimation }
}

export default useCircleProgressAnimation
